import { makeAutoObservable, runInAction } from 'mobx'
import { type IIconProps } from 'shared/ui'
import { logger } from 'shared/lib'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { ChatbotApi, type IChatbotFullData } from 'entities/Chatbot'
import { type ISmartCallFlowAITextingAgentStep } from 'entities/SmartCall/api/types'
import { FlowActionStore } from 'widgets/Flow'
import { SendFromBlockStore } from 'widgets/Flow/blocks/SendFrom'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import { type ISmartCallFlowActionType } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'

export type IAITextingAgentStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  childId?: ISmartCallFlowActionStoreProps['childId']
  args?: ISmartCallFlowAITextingAgentStep['args']
}

export class AITextingAgentStore {
  static readonly type = 'aiTextingAgent' satisfies ISmartCallFlowActionType
  static title = 'AI Texting Agent'
  static iconProps: IIconProps = {
    icon: 'stars',
    color: 'var(--purple-70)',
  }

  public readonly actionStore: ISmartCallActionStore

  sendFromBlockStore: SendFromBlockStore

  constructor(
    private _entities: IEntitiesStore,
    { args, id, ...props }: IAITextingAgentStoreProps
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: AITextingAgentStore.type,
      iconProps: AITextingAgentStore.iconProps,
      name: AITextingAgentStore.title,
      allowedNextActions: [
        'callingFlow',
        'sendVoicemail',
        'playSound',
        'ivrPhoneTree',
        'forwardCall',
        'sendMessage',
        'businessHours',
        'aiTextingAgent',
        'aiCallingAgent',
        'callQueue',
      ],
    })

    this.sendFromBlockStore = new SendFromBlockStore(this._entities, args, {
      personalInboxesOnly: true,
    })
    this._isCheckCriteria = Boolean(args?.applyFilters)

    if (args) {
      this.initByArgs(args)
    }

    makeAutoObservable(this)
  }

  get args(): ISmartCallFlowAITextingAgentStep['args'] {
    return {
      ...this.sendFromBlockStore.params,
      applyFilters: this.isCheckCriteria,
      agentId: (this._initAgentId || this.aiAgent?.id) ?? null,
    }
  }

  private _stepIdType: Pick<ISmartCallFlowAITextingAgentStep, 'id' | 'type'> = {
    id: 'aiAgentSync',
    type: 'action',
  }

  get step(): ISmartCallFlowAITextingAgentStep {
    return {
      ...this._stepIdType,
      args: this.args,
      next: this.actionStore.nextId,
    }
  }

  private _initAgentId: number | null = null
  initByArgs = async (args: ISmartCallFlowAITextingAgentStep['args']) => {
    try {
      if (args?.agentId) {
        this._initAgentId = args.agentId
        const res = await ChatbotApi.getChatbot(args.agentId)
        this.setAiAgent(res.data, true)
      }
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._initAgentId = null
      })
    }
  }

  validate = () => {
    let isValid = true
    const isValidSendFrom = this.sendFromBlockStore.validate()
    if (!isValidSendFrom) {
      isValid = false
    }
    if (!this.args?.agentId) {
      this.setAiAgentError('Required field')
      isValid = false
    } else {
      this.setAiAgentError('')
    }

    return isValid
  }

  get isConfigurationError() {
    return !!this.aiAgentError || !!this.sendFromBlockStore.error
  }

  setApiError = (key: string, message: string) => {
    if (key.includes('agentId')) {
      this.setAiAgentError(message)
    }
    if (key.includes('inboxId') || key.includes('sendingOptions')) {
      this.sendFromBlockStore.setError(message)
    }
  }

  private _aiAgentError = ''
  get aiAgentError() {
    return this._aiAgentError
  }
  setAiAgentError = (value: string) => {
    this._aiAgentError = value
  }

  private _aiAgent: IChatbotFullData | null = null
  get aiAgent() {
    return this._aiAgent
  }
  setAiAgent = (aiAgent: typeof this._aiAgent, noSetSendFrom?: boolean) => {
    if (aiAgent) {
      this.setAiAgentError('')
      const sendFrom = aiAgent.send_from
      if (sendFrom && !noSetSendFrom) {
        this.sendFromBlockStore.setError('')
        this.sendFromBlockStore.sendFromStore.setSendFrom(sendFrom)
      }
    }

    this._aiAgent = aiAgent
  }

  private _isCheckCriteria = false
  get isCheckCriteria() {
    return this._isCheckCriteria
  }
  toggleIsCheckCriteria = () => {
    this._isCheckCriteria = !this._isCheckCriteria
  }
}
